import React, { useEffect, useState } from 'react';
import { getProducts, createInvoice } from '../api';

const fmtRands = (cents) => `R${(cents / 100).toLocaleString('en-ZA', { minimumFractionDigits: 0 })}`;

export default function PurchaseModal({ onClose, onComplete }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [cart, setCart] = useState({});
  const [payMethod, setPayMethod] = useState('eft');
  const [invoice, setInvoice] = useState(null);

  useEffect(() => {
    getProducts()
      .then(p => {
        setProducts(p);
        const initial = {};
        p.forEach(prod => { initial[prod.id] = 0; });
        setCart(initial);
      })
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  const setQty = (productId, qty) => {
    setCart(prev => ({ ...prev, [productId]: Math.max(0, qty) }));
  };

  const getActiveTier = (product, qty) => {
    if (!product.bulk_tiers?.length || qty < 1) return null;
    const sorted = [...product.bulk_tiers].sort((a, b) => b.min_qty - a.min_qty);
    return sorted.find(t => qty >= t.min_qty) || null;
  };

  const calculateLine = (product, qty) => {
    if (qty < 1) return null;
    const tier = getActiveTier(product, qty);
    const bonus = tier ? tier.bonus_qty : 0;
    const total = qty * product.unit_price;
    return { product, qty, bonus, total, credits: qty + bonus };
  };

  const lines = products
    .map(p => calculateLine(p, cart[p.id] || 0))
    .filter(Boolean);

  const grandTotal = lines.reduce((sum, l) => sum + l.total, 0);
  const vatAmount = Math.round(grandTotal - grandTotal / 1.15);
  const subtotal = grandTotal - vatAmount;
  const hasItems = lines.length > 0;

  const handleSubmit = async () => {
    if (!hasItems) return;
    setError(null);
    setSubmitting(true);
    try {
      const items = lines.map(l => ({
        product_id: l.product.id,
        quantity: l.qty,
      }));
      const inv = await createInvoice({ items, payment_method: payMethod });
      setInvoice(inv);
      if (onComplete) onComplete(inv);
    } catch (err) {
      setError(err.message || 'Failed to create invoice');
    } finally {
      setSubmitting(false);
    }
  };

  if (invoice) {
    return (
      <div style={styles.overlay}>
        <div className="sq-dark-doc" style={styles.card}>
          <div style={styles.successIcon}>&#10003;</div>
          <div style={styles.cardTitle}>Invoice Created</div>
          <div style={styles.refBig}>{invoice.ref_number}</div>
          <div style={styles.totalBig}>{fmtRands(invoice.total_cents)}</div>

          {invoice.payment_method === 'eft' && (
            <div style={styles.bankingBox}>
              <div style={styles.bankingLabel}>Banking Details</div>
              <div style={styles.bankingNote}>
                Use the reference number above as your payment reference.
                Credits will be loaded once payment is confirmed.
              </div>
              <a
                href={`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/invoices/${invoice.id}/html`}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.downloadBtn}
              >
                Download Invoice with Banking Details
              </a>
            </div>
          )}

          <button onClick={onClose} style={styles.closeBtn}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay}>
      <div className="sq-dark-doc" style={styles.card}>
        <div style={styles.cardTitle}>Purchase Credits</div>
        <div style={styles.cardSubtitle}>Select products and quantities</div>

        {loading ? (
          <div style={styles.loadingText}>Loading products...</div>
        ) : (
          <>
            {/* Product rows */}
            <div style={styles.productList}>
              {products.map(product => {
                const qty = cart[product.id] || 0;
                const tier = getActiveTier(product, qty);
                const line = calculateLine(product, qty);

                return (
                  <div key={product.id} style={styles.productRow}>
                    <div style={styles.productInfo}>
                      <div style={styles.productName}>{product.label}</div>
                      <div style={styles.productPrice}>{fmtRands(product.unit_price)} each incl VAT</div>
                      {product.bulk_tiers?.length > 0 && (
                        <div style={styles.tierList}>
                          {product.bulk_tiers.map((t, i) => (
                            <span
                              key={i}
                              style={{
                                ...styles.tierChip,
                                ...(tier && tier.min_qty === t.min_qty ? styles.tierChipActive : {}),
                              }}
                            >
                              {t.label}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={styles.qtyControl}>
                      <button
                        onClick={() => setQty(product.id, qty - 1)}
                        style={styles.qtyBtn}
                        disabled={qty <= 0}
                      >
                        -
                      </button>
                      <div style={styles.qtyValue}>{qty}</div>
                      <button
                        onClick={() => setQty(product.id, qty + 1)}
                        style={styles.qtyBtn}
                      >
                        +
                      </button>
                    </div>
                    {line && (
                      <div style={styles.lineTotal}>
                        <div style={styles.lineTotalAmount}>{fmtRands(line.total)}</div>
                        <div style={styles.lineTotalCredits}>
                          {line.credits} credit{line.credits !== 1 ? 's' : ''}
                          {line.bonus > 0 && (
                            <span style={styles.bonusTag}> +{line.bonus} free</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Summary */}
            {hasItems && (
              <div style={styles.summary}>
                <div style={styles.summaryRow}>
                  <span>Subtotal (excl VAT)</span>
                  <span>{fmtRands(subtotal)}</span>
                </div>
                <div style={styles.summaryRow}>
                  <span>VAT (15%)</span>
                  <span>{fmtRands(vatAmount)}</span>
                </div>
                <div style={styles.summaryTotal}>
                  <span>Total</span>
                  <span>{fmtRands(grandTotal)}</span>
                </div>
              </div>
            )}


            {error && <div style={styles.error}>{error}</div>}

            {/* Actions */}
            <button
              onClick={handleSubmit}
              disabled={!hasItems || submitting}
              style={{ ...styles.submitBtn, opacity: hasItems && !submitting ? 1 : 0.5 }}
            >
              {submitting
                ? 'Creating invoice...'
                : hasItems
                ? `Generate Invoice — ${fmtRands(grandTotal)}`
                : 'Select products above'}
            </button>
          </>
        )}

        <button onClick={onClose} style={styles.cancelBtn} disabled={submitting}>
          Cancel
        </button>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.92)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    backdropFilter: 'blur(4px)',
    padding: 16,
    overflowY: 'auto',
  },
  card: {
    background: 'var(--white, #fff)',
    border: '2px solid var(--ink)',
    borderRadius: 10,
    padding: '28px 28px 20px',
    maxWidth: 520,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  cardTitle: {
    fontFamily: "'News Cycle', serif",
    fontSize: 24,
    fontWeight: 700,
    color: 'var(--ink)',
    textAlign: 'center',
  },
  cardSubtitle: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 11,
    color: 'var(--muted)',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    textAlign: 'center',
  },
  loadingText: {
    textAlign: 'center',
    color: 'var(--muted)',
    fontSize: 13,
    padding: '20px 0',
  },
  productList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  productRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '14px 16px',
    background: 'var(--cream)',
    borderRadius: 6,
    border: '1px solid var(--rule)',
  },
  productInfo: {
    flex: 1,
    minWidth: 0,
  },
  productName: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--ink)',
  },
  productPrice: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 11,
    color: 'var(--muted)',
    marginTop: 2,
  },
  tierList: {
    display: 'flex',
    gap: 4,
    marginTop: 6,
    flexWrap: 'wrap',
  },
  tierChip: {
    padding: '2px 8px',
    borderRadius: 10,
    fontSize: 10,
    fontFamily: "'DM Mono', monospace",
    background: 'var(--white)',
    border: '1px solid var(--rule)',
    color: 'var(--muted)',
  },
  tierChipActive: {
    background: 'rgba(26,122,74,0.1)',
    borderColor: 'var(--green)',
    color: 'var(--green)',
  },
  qtyControl: {
    display: 'flex',
    alignItems: 'center',
    gap: 0,
    flexShrink: 0,
  },
  qtyBtn: {
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--white)',
    border: '1px solid var(--rule)',
    fontSize: 16,
    fontWeight: 700,
    cursor: 'pointer',
    color: 'var(--ink)',
    fontFamily: "'Work Sans', sans-serif",
  },
  qtyValue: {
    width: 40,
    textAlign: 'center',
    fontFamily: "'News Cycle', serif",
    fontSize: 18,
    fontWeight: 700,
    color: 'var(--ink)',
  },
  lineTotal: {
    textAlign: 'right',
    flexShrink: 0,
    minWidth: 80,
  },
  lineTotalAmount: {
    fontFamily: "'News Cycle', serif",
    fontSize: 16,
    fontWeight: 700,
    color: 'var(--ink)',
  },
  lineTotalCredits: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 10,
    color: 'var(--muted)',
    marginTop: 2,
  },
  bonusTag: {
    color: 'var(--green)',
    fontWeight: 600,
  },
  summary: {
    padding: '12px 0',
    borderTop: '1px solid var(--rule)',
    borderBottom: '1px solid var(--rule)',
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 12,
    color: 'var(--muted)',
    padding: '4px 0',
    fontFamily: "'DM Mono', monospace",
  },
  summaryTotal: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 16,
    fontWeight: 700,
    color: 'var(--ink)',
    paddingTop: 8,
    marginTop: 4,
    borderTop: '2px solid var(--ink)',
    fontFamily: "'News Cycle', serif",
  },
  payMethodSection: {
    padding: '4px 0',
  },
  payMethodLabel: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 10,
    letterSpacing: '1px',
    textTransform: 'uppercase',
    color: 'var(--muted)',
    marginBottom: 8,
  },
  payMethodRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 8,
  },
  payMethodBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 12px',
    background: 'var(--cream)',
    border: '1px solid var(--rule)',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 12,
    color: 'var(--muted)',
    fontFamily: "'Work Sans', sans-serif",
  },
  payMethodBtnActive: {
    borderColor: 'var(--accent)',
    background: 'rgba(255,106,61,0.1)',
    color: 'var(--ink)',
  },
  payMethodIcon: {
    fontSize: 16,
  },
  error: {
    fontSize: 12,
    color: 'var(--red)',
    fontFamily: "'DM Mono', monospace",
    textAlign: 'center',
  },
  submitBtn: {
    padding: '14px',
    background: 'var(--accent)',
    color: '#14110e',
    border: 'none',
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Work Sans', sans-serif",
    letterSpacing: '0.3px',
  },
  cancelBtn: {
    padding: '10px',
    background: 'transparent',
    border: '1px solid var(--rule)',
    borderRadius: 6,
    fontSize: 13,
    color: 'var(--muted)',
    cursor: 'pointer',
    fontFamily: "'Work Sans', sans-serif",
  },
  successIcon: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    background: 'rgba(26,122,74,0.1)',
    color: 'var(--green)',
    fontSize: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
  },
  refBig: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 16,
    fontWeight: 600,
    color: 'var(--ink)',
    textAlign: 'center',
    letterSpacing: '0.5px',
  },
  totalBig: {
    fontFamily: "'News Cycle', serif",
    fontSize: 32,
    fontWeight: 700,
    color: 'var(--ink)',
    textAlign: 'center',
  },
  bankingBox: {
    background: 'var(--cream)',
    border: '1px solid var(--rule)',
    borderRadius: 6,
    padding: 16,
    textAlign: 'center',
  },
  bankingLabel: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 10,
    letterSpacing: '1px',
    textTransform: 'uppercase',
    color: 'var(--muted)',
    marginBottom: 8,
  },
  bankingNote: {
    fontSize: 12,
    color: 'var(--muted)',
    lineHeight: 1.6,
    marginBottom: 12,
  },
  downloadBtn: {
    display: 'inline-block',
    padding: '10px 18px',
    background: 'var(--accent)',
    color: '#14110e',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    textDecoration: 'none',
    fontFamily: "'Work Sans', sans-serif",
  },
  closeBtn: {
    padding: '12px',
    background: 'var(--accent)',
    color: '#14110e',
    border: 'none',
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Work Sans', sans-serif",
  },
};
